// The only reader and writer of the deferral deadline. `sessionStorage` throws
// in restricted contexts (third-party iframe, blocked cookies) and returns
// same-origin *mutable* strings, which is not a trust boundary — so this parse
// function is the control, not an assertion that the value is trustworthy.

import { APPLY_ESCALATION_MS } from "./updateApplyPolicy";

const STORAGE_KEY = "ruralops:updateDeferredAt";

/**
 * Widest deferral age accepted. Anything older is either clock manipulation or a
 * leftover from a previous session; either way it must not be able to suppress
 * escalation indefinitely or force one immediately.
 */
const MAX_DEFERRAL_AGE_MS = APPLY_ESCALATION_MS * 4;

export function clearDeferredSince(): void {
	try {
		sessionStorage.removeItem(STORAGE_KEY);
	} catch {
		// Restricted context — nothing was ever written, nothing to clear.
	}
}

function discard(): null {
	clearDeferredSince();
	return null;
}

/**
 * Returns the epoch-ms the deferral for `swRevision` started, or `null` when the
 * stored value is absent, unreadable, malformed, out of range, or belongs to a
 * different service-worker revision. Any violation also deletes the key, so a
 * poisoned value cannot be re-read on the next tick.
 *
 * Value shape: `"<swRevision>:<epochMs>"`.
 */
export function readDeferredSince(swRevision: string): number | null {
	let raw: string | null;
	try {
		raw = sessionStorage.getItem(STORAGE_KEY);
	} catch {
		// Storage is unavailable, so there is no key to delete and no deadline to
		// trust: the caller falls back to "not deferred yet".
		return null;
	}
	if (raw === null) return null;

	// The epoch is the last segment, so a revision containing ":" still parses.
	const separator = raw.lastIndexOf(":");
	if (separator <= 0) return discard();

	const revision = raw.slice(0, separator);
	if (revision !== swRevision) return discard();

	const atMs = Number(raw.slice(separator + 1));
	// Rejects NaN, ±Infinity, fractions and anything beyond 2^53-1.
	if (!Number.isSafeInteger(atMs)) return discard();

	const now = Date.now();
	if (atMs > now) return discard();
	if (now - atMs > MAX_DEFERRAL_AGE_MS) return discard();

	return atMs;
}

export function writeDeferredSince(swRevision: string, atMs: number): void {
	try {
		sessionStorage.setItem(STORAGE_KEY, `${swRevision}:${atMs}`);
	} catch {
		// Restricted context — the deadline degrades to in-memory only, which is
		// safe: a lost deferral start means the policy defers again, never applies.
	}
}
